'use client';

import { IconFrenchFlag, IconPortugueseFlag, IconSpanishFlag, IconUsaFlag } from '@/components/ui/icons';
import { usePathname, useRouter } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { ChevronDown } from 'lucide-react';
import type { Locale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { JSX, useEffect, useRef, useState, useTransition } from 'react';

type Props = {
    defaultValue: string;
    label: string;
    className?: string;
    isDirectionUp?: boolean;
};

const FLAGS: ReadonlyArray<{ locale: string; icon: JSX.Element }> = [
    { locale: 'fr', icon: <IconFrenchFlag className="mr-3" /> },
    { locale: 'pt', icon: <IconPortugueseFlag className="mr-3" /> },
    { locale: 'es', icon: <IconSpanishFlag className="mr-3" /> },
    { locale: 'en', icon: <IconUsaFlag className="mr-3" /> },
];

export const LocaleSwitcherSelect = ({ defaultValue, label, className = '', isDirectionUp }: Props) => {
    const router = useRouter();
    const [isPending, startTransition] = useTransition();
    const pathname = usePathname();
    const params = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const t = useTranslations('LocaleSwitcher');

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen]);

    const currentLocale = routing.locales.find((cur) => defaultValue === cur);

    const onSelectChange = (nextLocale: Locale) => {
        setIsOpen(false);
        startTransition(() => {
            router.replace(
                // @ts-expect-error -- TypeScript will validate that only known `params`
                // are used in combination with a given `pathname`. Since the two will
                // always match for the current route, we can skip runtime checks.
                { pathname, params },
                { locale: nextLocale },
            );
        });
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <p className="sr-only">{label}</p>
            <button
                type="button"
                className={`
                    px-3 py-3 rounded-[8px] cursor-pointer flex items-center
                    transition-colors text-white hover:text-gray-400 duration-300
                    bg-[transparent]
                `}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="pr-[10px]">{t('locale', { locale: currentLocale || 'fr' })}</span>
                <ChevronDown className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} size={24} />
            </button>

            <ul
                className={`
                    px-[10px] pt-1 pb-[0.5px] absolute left-0 bg-[rgba(14,14,14,1)] rounded-[8px] z-1
                    ${isDirectionUp ? 'bottom-full mb-2' : 'top-full mt-2'}
                    ${isPending ? 'pointer-events-none opacity-60' : ''}
                    ${isOpen ? 'block' : 'hidden'}
                `}
            >
                {routing.locales.map((cur, index, array) => (
                    <li
                        className={`
                            flex items-center gap-2 rounded-[8px] p-[10px] my-2 relative text-base
                            cursor-pointer transition-colors duration-200
                            after:content-[''] after:absolute after:-bottom-1 after:left-0
                            after:w-full after:h-[1px] after:bg-[rgba(255,255,255,0.10)]
                            ${cur === currentLocale ? 'bg-[#DD06AE] hover:bg-[#DD06AE]' : 'hover:bg-[rgba(255,255,255,0.30)]'}
                            ${index === array.length - 1 ? 'after:content-[none]' : ''}
                        `}
                        key={cur}
                        aria-selected={cur === currentLocale}
                        onClick={() => onSelectChange(cur)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelectChange(cur);
                            }
                        }}
                    >
                        {FLAGS.find((f) => f.locale === cur)?.icon}
                        {t('locale', { locale: cur })}
                    </li>
                ))}
            </ul>
        </div>
    );
};
